import type { UseQueryResult, DefaultError } from '@tanstack/react-query';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../clients';
import { queryKeys } from '../../lib/query-keys';
import type { Student, StudentPlan, StudentEnrollment } from '../../types';
import {
  getStudentCoursePlanDetailPath,
  getStudentCoursePlansByStudentPath,
  getStudentCoursePlansBasePath,
  getStudentCoursePlansBasePathForHighschool,
  getStudentCoursePlansSetCurrentPath,
  getStudentCoursePlansApprovalPath,
  getStudentCoursePlanSignPath,
  getStudentCoursePlanRequirementsPath,
  getStudentCoursePlanRequirementPath,
  getStudentCoursePlanCoursePath,
  getStudentCoursePlanClustersPathwaysPath,
  getValidateCourseRequisitesPath,
} from '../../utils/student/student-api-paths';
import type {
  ValidateCourseRequisitesRequest,
  RequisitesValidationResult,
} from '../../features/student/shared/types/requisite-course.types';
import type { CourseSummary } from '@/types/course';
import { isLocalHostRuntime } from '../../utils/env.utils';
import { isStudentCoursePlanManagerEnabled } from '../../utils/cp-context';
import { z } from 'zod';

const approvalDecisionResponseSchema = z.object({ status: z.string().trim().min(1) });

function getLocalFallbackStudent(): Student {
  return {
    id: '1000',
    studentId: '1000',
    firstName: 'Local',
    lastName: 'Student',
    email: 'local.student@example.com',
    major: '',
    expectedGraduation: '',
    enrollmentStatus: 'active',
    creditsCompleted: 0,
    creditsInProgress: 0,
  };
}

export interface ManageCoursePlansItem {
  id: string;
  name: string;
  status: string;
  isActive?: boolean;
  planOfStudy?: { id?: string };
  lastEdited?: number;
  schoolId?: string;
  clusterIds?: string[];
  credits?: {
    inYourPlan?: number;
    required?: number;
  };
  pathways?: Array<{ id: string; name?: string; clusterName?: string }>;
}

export interface StudentCoursePlanClusterPathwayItem {
  clusterId?: string;
  clusterName?: string;
  pathwayId?: string;
  pathwayName?: string;
}

export interface CreateManageCoursePlanPayload {
  planOfStudy?: { id: string };
  studentId: string;
  clusters: Array<{ id: string; name: string }>;
  pathways: Array<{ id: string; name: string; clusterId: string }>;
  studentDetails: Record<string, unknown>;
  schoolId: string;
  baseStudentCoursePlanId?: string;
}

export interface UpdateRequirementsPayload {
  requirements: Array<{
    id: string;
    requirementCourses: {
      mandatedCourses: unknown[];
      groupCourses: unknown[];
      choicesByGradeCourses: Array<{
        grade: number;
        courses: unknown[][];
      }>;
      itemsFromAListCourses: unknown[];
    };
  }>;
  creditRecovery: unknown;
  courseChanges: unknown[];
}

/**
 * Payload for PUT `/student-course-plans/:planId/requirement` (singular).
 * Legacy CPUI IFAL target delete and recovery-credit updates use this shape.
 */
export interface UpdateRequirementPayload {
  requirement: UpdateRequirementsPayload['requirements'][number];
  creditRecovery: unknown;
}

export interface SetPlanApprovalDecisionPayload {
  approved: boolean;
  action?: 'approve' | 'reopen';
  parentInfo?: {
    parentFullName: string;
    parentApprovalAt: Date | string;
  };
}

/**
 * Repository for student-related data operations.
 */
export const studentRepository = {
  /**
   * Fetch current student profile
   */
  useGetCurrentStudent(): UseQueryResult<Student, DefaultError> {
    const apiClient = useApiClient();

    return useQuery({
      queryKey: queryKeys.me,
      queryFn: async () => {
        if (import.meta.env.DEV && import.meta.env.VITE_USE_MOCKS === 'true') {
          const mockData = await import('../../mock-data/student.json');
          return mockData.default as Student;
        }

        try {
          const response = await apiClient.get<Student>('/users/me');
          return response.data;
        } catch (error) {
          if (isLocalHostRuntime()) {
            return getLocalFallbackStudent();
          }
          throw error;
        }
      },
    });
  },

  /**
   * Fetch a student by ID (for staff view)
   */
  useGetStudent(studentId: string, enabled = true): UseQueryResult<Student, DefaultError> {
    const apiClient = useApiClient();

    return useQuery({
      queryKey: queryKeys.students.detail(studentId),
      queryFn: async () => {
        if (import.meta.env.DEV && import.meta.env.VITE_USE_MOCKS === 'true') {
          const mockData = await import('../../mock-data/student.json');
          return mockData.default as Student;
        }

        const response = await apiClient.get<Student>(`/students/${studentId}`);
        return response.data;
      },
      enabled,
    });
  },

  /**
   * Fetch all plans for a student
   */
  useGetStudentPlans(studentId: string): UseQueryResult<StudentPlan[], DefaultError> {
    const apiClient = useApiClient();

    return useQuery({
      queryKey: queryKeys.students.plans(studentId),
      queryFn: async () => {
        if (import.meta.env.DEV && import.meta.env.VITE_USE_MOCKS === 'true') {
          const mockData = await import('../../mock-data/plans.json');
          return mockData.default as StudentPlan[];
        }

        const response = await apiClient.get<StudentPlan[]>(`/students/${studentId}/plans`);
        return response.data;
      },
    });
  },

  /**
   * Fetch a specific plan
   */
  useGetPlan(
    studentId: string,
    planId: string,
    enabled = true
  ): UseQueryResult<StudentPlan, DefaultError> {
    const apiClient = useApiClient();

    return useQuery({
      queryKey: queryKeys.students.plan(studentId, planId),
      queryFn: async () => {
        if (import.meta.env.DEV && import.meta.env.VITE_USE_MOCKS === 'true') {
          const mockData = await import('../../mock-data/plans.json');
          const plans = mockData.default as StudentPlan[];
          const plan = plans.find((p) => p.id === planId);
          if (!plan) {
            throw new Error(`Plan ${planId} not found`);
          }
          return plan;
        }

        const response = await apiClient.get<StudentPlan>(`/students/${studentId}/plans/${planId}`);
        return response.data;
      },
      enabled,
    });
  },

  /**
   * Create a new plan
   */
  useCreatePlan(studentId: string) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (
        plan: Omit<StudentPlan, 'id' | 'studentId' | 'createdAt' | 'updatedAt'>
      ) => {
        const response = await apiClient.post<StudentPlan>(`/students/${studentId}/plans`, plan);
        return response.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.students.plans(studentId) });
      },
    });
  },

  /**
   * Create a student course plan using the legacy student endpoint contract.
   * Calls POST /student-course-plans (proxy: /course-planner-next/student-course-plans).
   */
  useCreateManageCoursePlan(studentId: string | undefined) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (payload: CreateManageCoursePlanPayload) => {
        let planOfStudyId = payload.planOfStudy?.id;

        if (!planOfStudyId && payload.baseStudentCoursePlanId && payload.schoolId) {
          const withPosPath = `${getStudentCoursePlansBasePathForHighschool(payload.schoolId)}/student/${encodeURIComponent(payload.studentId)}?withPos=true`;
          const withPosResponse = await apiClient.get<ManageCoursePlansItem[]>(withPosPath);
          const plansWithPos = Array.isArray(withPosResponse.data) ? withPosResponse.data : [];
          const basePlan = plansWithPos.find((plan) => plan.id === payload.baseStudentCoursePlanId);
          planOfStudyId = basePlan?.planOfStudy?.id;
        }

        if (!planOfStudyId && isLocalHostRuntime()) {
          planOfStudyId = import.meta.env.VITE_STUDENT_PLAN_OF_STUDY_ID;
        }

        if (!planOfStudyId) {
          throw new Error(
            'Unable to resolve plan of study for new plan creation. In local mode, set VITE_STUDENT_PLAN_OF_STUDY_ID in apps/ui/.env.'
          );
        }

        const requestBody: CreateManageCoursePlanPayload = {
          ...payload,
          planOfStudy: { id: planOfStudyId },
        };

        const response = await apiClient.post(getStudentCoursePlansBasePath(), requestBody);
        const responseData: unknown = response.data;
        const id =
          responseData !== null &&
          typeof responseData === 'object' &&
          'id' in responseData &&
          typeof (responseData as Record<string, unknown>).id === 'string'
            ? (responseData as Record<string, unknown>).id
            : undefined;
        return { id: id as string | undefined };
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.studentCoursePlans.list(studentId ?? ''),
        });
      },
    });
  },

  /**
   * Update an existing plan
   */
  useUpdatePlan(studentId: string) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async ({ planId, ...updates }: Partial<StudentPlan> & { planId: string }) => {
        const response = await apiClient.patch<StudentPlan>(
          `/students/${studentId}/plans/${planId}`,
          updates
        );
        return response.data;
      },
      onSuccess: (updatedPlan) => {
        queryClient.setQueryData(queryKeys.students.plan(studentId, updatedPlan.id), updatedPlan);
        queryClient.invalidateQueries({ queryKey: queryKeys.students.plans(studentId) });
      },
    });
  },

  /**
   * Fetch student enrollments
   */
  useGetEnrollments(studentId: string): UseQueryResult<StudentEnrollment[], DefaultError> {
    const apiClient = useApiClient();

    return useQuery({
      queryKey: queryKeys.students.enrollments(studentId),
      queryFn: async () => {
        if (import.meta.env.DEV && import.meta.env.VITE_USE_MOCKS === 'true') {
          const mockData = await import('../../mock-data/enrollments.json');
          return mockData.default as StudentEnrollment[];
        }

        const response = await apiClient.get<StudentEnrollment[]>(
          `/students/${studentId}/enrollments`
        );
        return response.data;
      },
    });
  },

  /**
   * Enroll in a section
   */
  useEnrollInSection(studentId: string) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (sectionId: string) => {
        const response = await apiClient.post<StudentEnrollment>(
          `/students/${studentId}/enrollments`,
          { sectionId }
        );
        return response.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.students.enrollments(studentId),
        });
      },
    });
  },

  /**
   * Drop enrollment
   */
  useDropEnrollment(studentId: string) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (enrollmentId: string) => {
        await apiClient.delete(`/students/${studentId}/enrollments/${enrollmentId}`);
        return enrollmentId;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.students.enrollments(studentId),
        });
      },
    });
  },

  /**
   * Fetch all course plans for a student using the legacy endpoint contract.
   * Calls GET /course-planner-next/student-course-plans/student/:studentId
   */
  useGetManageCoursePlans(
    studentId: string | undefined,
    enabled = true
  ): UseQueryResult<ManageCoursePlansItem[], DefaultError> {
    const apiClient = useApiClient();

    const getMockManageCoursePlans = async (): Promise<ManageCoursePlansItem[]> => {
      const mockData = await import('../../mock-data/manage-course-plans.json');
      return mockData.default as ManageCoursePlansItem[];
    };

    const isLocalHostRuntime =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    return useQuery({
      queryKey: queryKeys.studentCoursePlans.list(studentId ?? ''),
      queryFn: async () => {
        if (import.meta.env.DEV && import.meta.env.VITE_USE_MOCKS === 'true') {
          return getMockManageCoursePlans();
        }

        try {
          const path = getStudentCoursePlansByStudentPath(studentId!);
          const response = await apiClient.get<ManageCoursePlansItem[]>(path);
          return Array.isArray(response.data) ? response.data : [];
        } catch (error) {
          if (isLocalHostRuntime) {
            return getMockManageCoursePlans();
          }
          throw error;
        }
      },
      enabled: Boolean(studentId) && enabled,
      retry: false,
      throwOnError: false,
    });
  },

  /**
   * Delete a student course plan.
   * Calls DELETE /course-planner-next/student-course-plans/:planId
   */
  useDeleteManageCoursePlan(studentId: string | undefined) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (planId: string) => {
        await apiClient.delete(getStudentCoursePlanDetailPath(planId));
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.studentCoursePlans.list(studentId ?? ''),
        });
      },
    });
  },

  /**
   * Set a draft plan as current.
   * Calls PUT /course-planner-next/student-course-plans/set-current
   */
  useSetCurrentManageCoursePlan(studentId: string | undefined) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (planId: string) => {
        const normalizedStudentId = studentId ? String(studentId) : '';
        await apiClient.put(getStudentCoursePlansSetCurrentPath(), {
          planId: String(planId),
          studentId: normalizedStudentId,
        });
      },
      onSuccess: () => {
        // Invalidate all studentCoursePlans queries (list + every cached detail)
        // so both the newly-current plan and the formerly-current plan refresh
        // their isActive flag without requiring a full page reload.
        queryClient.invalidateQueries({
          queryKey: queryKeys.studentCoursePlans.all,
        });
      },
    });
  },

  /**
   * Submit a plan for approval.
   * Calls POST /course-planner-next/student-course-plans/approval
   * with { planOfStudyId, approved: true, action: 'submit' }.
   *
   * @param planId - The student course plan ID (used as planOfStudyId)
   */
  useSubmitPlanForApproval(planId: string | undefined) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async () => {
        await apiClient.post(getStudentCoursePlansApprovalPath(), {
          planOfStudyId: planId,
          approved: true,
          ...(isStudentCoursePlanManagerEnabled(true) ? { action: 'submit' } : {}),
        });
      },
      onSuccess: () => {
        // Invalidate all plan queries so status updates are reflected immediately
        const invalidation = queryClient.invalidateQueries({
          queryKey: queryKeys.studentCoursePlans.all,
        });
        if (isStudentCoursePlanManagerEnabled(true)) {
          return invalidation;
        }
      },
    });
  },

  /**
   * Set an approval decision for a student plan.
   * Calls POST /course-planner-next/student-course-plans/approval
   * with { planOfStudyId, approved, action? }.
   * Parent approval can include parentInfo to preserve legacy signature behavior.
   *
   * @param planId - The student course plan ID (used as planOfStudyId)
   */
  useSetPlanApprovalDecision(planId: string | undefined) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (input: boolean | SetPlanApprovalDecisionPayload) => {
        const payload: SetPlanApprovalDecisionPayload =
          typeof input === 'boolean' ? { approved: input } : input;

        const response = await apiClient.post<unknown>(getStudentCoursePlansApprovalPath(), {
          planOfStudyId: planId,
          approved: payload.approved,
          ...(isStudentCoursePlanManagerEnabled(true) && payload.action
            ? { action: payload.action }
            : {}),
          ...(payload.parentInfo ? { parentInfo: payload.parentInfo } : {}),
        });
        if (!isStudentCoursePlanManagerEnabled(true)) {
          return;
        }
        return approvalDecisionResponseSchema.parse(response.data).status;
      },
      onSuccess: () => {
        // Invalidate all plan queries so status updates are reflected immediately
        const invalidation = queryClient.invalidateQueries({
          queryKey: queryKeys.studentCoursePlans.all,
        });
        if (isStudentCoursePlanManagerEnabled(true)) {
          return invalidation;
        }
      },
    });
  },

  /**
   * Trigger signed-document generation after a parent approval decision.
   * Calls GET /course-planner-next/student-course-plans/{id}/sign?approved={bool}&parentName={name}.
   * This matches the legacy CPUI ON_SIGN saga and generates the document saved to
   * "About me > Documents shared with me" and emailed to the parent.
   *
   * Uses apiClient to reach blue-ridge directly with Bearer auth. The page origin cannot be used
   * because it returns SPA shell (HTML) for unrecognized paths, which would incorrectly complete
   * the request without triggering document generation.
   *
   * @param planId - The student course plan ID
   */
  useSignStudentCoursePlan(planId: string | undefined) {
    const apiClient = useApiClient();

    return useMutation({
      mutationFn: async ({ approved, parentName }: { approved: boolean; parentName: string }) => {
        if (!planId) {
          return;
        }

        const signPath = getStudentCoursePlanSignPath(planId, approved, parentName);

        // Call blue-ridge directly via apiClient with Bearer auth to trigger document generation
        await apiClient.get(signPath);
      },
    });
  },

  /**
   * Calls PUT /student-course-plans/:planId/add-courses.
   * Same contract as legacy CPUI after validate-course-requisites: `courses` are merged into the plan with inPlan.
   * Invalidates the plan detail query on success.
   */
  useAddCoursesToPlan(planId: string | undefined) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (courses: CourseSummary[]) => {
        const path = `${getStudentCoursePlanDetailPath(planId!)}/add-courses`;
        const response = await apiClient.put(path, { courses });
        return response.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.studentCoursePlans.detail(planId ?? ''),
        });
      },
    });
  },

  /**
   * Fetch a specific student course plan by its ID.
   * Calls GET /student-course-plans/:planId (via getStudentCoursePlanDetailPath).
   * Returns the raw API response; the caller is responsible for transforming it.
   */
  useGetStudentCoursePlan(
    planId: string | undefined,
    enabled = true
  ): UseQueryResult<StudentCoursePlanApiResponse, DefaultError> {
    const apiClient = useApiClient();

    const getMockStudentCoursePlan = async (): Promise<StudentCoursePlanApiResponse> => {
      const mockData = await import('../../mock-data/student-course-plan.json');
      return mockData.default as StudentCoursePlanApiResponse;
    };

    const isLocalHostRuntime =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    return useQuery({
      queryKey: queryKeys.studentCoursePlans.detail(planId ?? ''),
      queryFn: async () => {
        if (import.meta.env.DEV && import.meta.env.VITE_USE_MOCKS === 'true') {
          return getMockStudentCoursePlan();
        }

        try {
          const path = getStudentCoursePlanDetailPath(planId!);
          const response = await apiClient.get<StudentCoursePlanApiResponse>(path);
          return response.data;
        } catch (error) {
          if (isLocalHostRuntime) {
            return getMockStudentCoursePlan();
          }
          throw error;
        }
      },
      enabled: Boolean(planId) && enabled,
      retry: false,
      throwOnError: false,
    });
  },

  /**
   * Fetch cluster/pathway entries assigned to a student course plan.
   * Calls GET /student-course-plans/:id/clusters-pathways
   */
  useGetStudentCoursePlanClustersPathways(
    planId: string | undefined,
    enabled = true
  ): UseQueryResult<StudentCoursePlanClusterPathwayItem[], DefaultError> {
    const apiClient = useApiClient();

    return useQuery({
      queryKey: queryKeys.studentCoursePlans.clustersPathways(planId ?? ''),
      queryFn: async () => {
        if (!planId) {
          return [];
        }

        try {
          const response = await apiClient.get<StudentCoursePlanClusterPathwayItem[]>(
            getStudentCoursePlanClustersPathwaysPath(planId)
          );
          return Array.isArray(response.data) ? response.data : [];
        } catch (error) {
          if (isLocalHostRuntime()) {
            return [];
          }
          throw error;
        }
      },
      enabled: Boolean(planId) && enabled,
      retry: false,
      throwOnError: false,
    });
  },

  /**
   * Remove a course from a student course plan by updating its requirements.
   * Calls PUT /student-course-plans/:planId/requirements with the full
   * requirements payload where the target course has inPlan set to false.
   *
   * @param planId - The student course plan ID
   * @returns A mutation that accepts the requirements payload with courseChanges
   */
  useRemoveCourseFromPlan(planId: string | undefined) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (payload: UpdateRequirementsPayload) => {
        const path = getStudentCoursePlanRequirementsPath(planId!);
        const response = await apiClient.put(path, payload);
        return response.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.studentCoursePlans.detail(planId ?? ''),
        });
      },
    });
  },

  /**
   * Update a student course plan's requirements (add or remove courses).
   * Semantic alias for {@link useRemoveCourseFromPlan} — the underlying endpoint
   * (PUT /student-course-plans/:planId/requirements) handles both additions and
   * removals depending on the `inPlan` flag in each course object.
   */
  useUpdateRequirements(planId: string | undefined) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (payload: UpdateRequirementsPayload) => {
        const path = getStudentCoursePlanRequirementsPath(planId!);
        const response = await apiClient.put(path, payload);
        return response.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.studentCoursePlans.detail(planId ?? ''),
        });
      },
    });
  },

  /**
   * Update a single student course plan requirement (legacy CPUI `updateRequirement`).
   * PUT `/student-course-plans/:planId/requirement` — used for IFAL target delete and recovery credit.
   */
  useUpdateRequirement(planId: string | undefined) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (payload: UpdateRequirementPayload) => {
        const path = getStudentCoursePlanRequirementPath(planId!);
        const response = await apiClient.put(path, payload);
        return response.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.studentCoursePlans.detail(planId ?? ''),
        });
      },
    });
  },

  /**
   * Remove one course from a plan (legacy CPUI `removeCourse` / `removeGivenCourses`).
   * DELETE `/student-course-plans/:planId/course/:courseId?courseStatementUnique=…`
   */
  useDeleteCourseFromStudentPlan(planId: string | undefined) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (course: { id: string; statementUnique?: string }) => {
        const path = getStudentCoursePlanCoursePath(
          planId!,
          course.id,
          course.statementUnique ?? ''
        );
        const response = await apiClient.delete(path);
        return response.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.studentCoursePlans.detail(planId ?? ''),
        });
      },
    });
  },

  /**
   * Validate prerequisite / co-requisite selections before adding courses to the plan.
   */
  useValidateCourseRequisites() {
    const apiClient = useApiClient();

    return useMutation({
      mutationFn: async (
        payload: ValidateCourseRequisitesRequest
      ): Promise<RequisitesValidationResult> => {
        if (import.meta.env.DEV && import.meta.env.VITE_USE_MOCKS === 'true') {
          return { errors: false };
        }
        const response = await apiClient.post<RequisitesValidationResult>(
          getValidateCourseRequisitesPath(),
          payload
        );
        return response.data;
      },
    });
  },

  useUpdateStudentCoursePlanSpecializations(planId: string | undefined) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (payload: {
        id: string;
        clusters: Array<{ id: string; name: string }>;
        pathways: Array<{ id: string; name: string; clusterId: string; requirements: unknown[] }>;
      }) => {
        const response = await apiClient.put(getStudentCoursePlansBasePath(), payload);
        return response.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.studentCoursePlans.detail(planId ?? ''),
        });
      },
    });
  },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type StudentCoursePlanApiResponse = Record<string, any>;
